import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "sonner";
import "./index.css";
import { DesktopAuthListener } from "@/components/desktop-auth-listener";
import { DesktopUpdatePrompt } from "@/components/desktop-update-prompt";
import { initAnalytics, track } from "@/lib/analytics";
import { getDesktopBridge } from "@/lib/desktop";
import { installGlobalErrorReporting, reportError } from "@/lib/report-error";
import { queryClient } from "@/lib/trpc";
import { router } from "@/router";

// Lets CSS add native window affordances (drag regions, traffic-light inset).
const bridge = getDesktopBridge();
if (bridge) {
	document.documentElement.dataset.desktop = bridge.platform;
}

installGlobalErrorReporting();
initAnalytics();

// One page view per route change, named by route id ("/app/boards/$boardId"),
// never the raw path (it carries ids).
let lastRouteId: string | null = null;
router.subscribe("onResolved", () => {
	const routeId = router.state.matches.at(-1)?.routeId ?? null;
	if (!routeId || routeId === lastRouteId) {
		return;
	}
	track("page.viewed", {
		route: routeId,
		from_route: lastRouteId ?? undefined,
	});
	lastRouteId = routeId;
});

// Failed data loads surface as toasts (see lib/trpc.ts); record which ones.
queryClient.getQueryCache().subscribe((event) => {
	if (event.type !== "updated" || event.action.type !== "error") {
		return;
	}
	const [path] = event.query.queryKey;
	const error = event.action.error as { data?: { code?: unknown } } | null;
	track("error.shown", {
		kind: "query",
		query: Array.isArray(path) ? path.join(".") : String(path),
		code: typeof error?.data?.code === "string" ? error.data.code : undefined,
	});
});

const container = document.getElementById("root");
if (!container) {
	throw new Error("Missing #root element");
}

createRoot(container, {
	// Render errors, including ones a route's error screen caught.
	onCaughtError: (error, info) => {
		reportError(error, {
			kind: "react.caught",
			componentStack: info.componentStack,
		});
	},
	onUncaughtError: (error, info) => {
		reportError(error, {
			kind: "react.uncaught",
			componentStack: info.componentStack,
		});
	},
}).render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
			<RouterProvider router={router} />
			<DesktopAuthListener />
			<DesktopUpdatePrompt />
			<Toaster position="bottom-right" richColors />
		</QueryClientProvider>
	</StrictMode>
);
